import { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeCtx = createContext({ theme: "light", toggle: () => {}, setTheme: () => {} });

// Public routes (SharePage + PublicHome) default anonymous visitors to
// DARK mode and persist that choice under a SEPARATE localStorage key
// so a logged-in Editor user's own theme preference is untouched even
// if they later visit a share link. If a session is present (`gf_user`
// cached), we treat the visit as a logged-in user reaching a public
// URL — they keep their own theme.
const EDITOR_STORAGE_KEY = "gf-theme";
const PUBLIC_STORAGE_KEY = "gf-theme-public";

function detectPublicScope() {
  try {
    const path = window.location.pathname || "/";
    const isSharePath = path.startsWith("/share/");
    const isPublicHome = path === "/" || path === "/index.html";
    const hasSession = !!localStorage.getItem("gf_user");
    // Public scope = anonymous visitor on a public route.
    return (isSharePath || isPublicHome) && !hasSession;
  } catch { return false; }
}

export function ThemeProvider({ children }) {
  const [publicScope] = useState(detectPublicScope);
  const storageKey = publicScope ? PUBLIC_STORAGE_KEY : EDITOR_STORAGE_KEY;

  const [theme, setThemeState] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved === "dark" || saved === "light") return saved;
      // Fresh anonymous visit on a public route → DARK by default.
      if (publicScope) return "dark";
      return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
    } catch { return publicScope ? "dark" : "light"; }
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    try { localStorage.setItem(storageKey, theme); } catch { /* ignore */ }
  }, [theme, storageKey]);

  const setTheme = useCallback((t) => setThemeState(t), []);
  const toggle = useCallback(() => setThemeState((t) => (t === "dark" ? "light" : "dark")), []);

  return <ThemeCtx.Provider value={{ theme, toggle, setTheme }}>{children}</ThemeCtx.Provider>;
}

export function useTheme() {
  return useContext(ThemeCtx);
}
